import {
  Box,
  Center,
  Flex,
  Heading,
  Icon,
  ModalBody,
  ModalContent,
  ModalHeader,
  Skeleton,
  Spacer,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { useDynamicModalContext } from "@umami/components";
import {
  type Provider,
  type QuoteRequest,
  type QuoteResponse,
  RampProviderError,
  getQuote,
} from "@umami/ramp";
import { useCustomToast } from "@umami/utils";
import { useCallback, useEffect, useState } from "react";

import { ModalBackButton } from "../BackButton";
import { type QuoteParams, getProviderIcon, supportedProviders } from "../Buy/FormPage";
import { ModalCloseButton } from "../CloseButton";

import { useColor } from "@/styles/useColor";

type ProviderSelectModalProps = {
  amount: string;
  providers: Provider[];
  quoteParams: QuoteParams;
};

const REFETCH_INTERVAL = 30000; // 30 seconds

export const ProviderSelectModal = ({
  amount,
  providers: initialProviders,
  quoteParams,
}: ProviderSelectModalProps) => {
  const color = useColor();
  const { goBack } = useDynamicModalContext();
  const toast = useCustomToast();

  const [debouncedAmount, setDebouncedAmount] = useState(amount);
  const [countdown, setCountdown] = useState(REFETCH_INTERVAL / 1000);

  // Only debounce when amount changes after initial mount
  useEffect(() => {
    if (amount !== debouncedAmount) {
      const timer = setTimeout(() => {
        setDebouncedAmount(amount);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [amount, debouncedAmount]);

  const fetchQuotes = useCallback(async (): Promise<Provider[]> => {
    const amountValue = parseFloat(debouncedAmount);

    if (!debouncedAmount || isNaN(amountValue) || amountValue <= 0) {
      return [];
    }

    const quoteRequests: QuoteRequest[] = supportedProviders.map(provider => ({
      provider,
      cryptoCurrency: quoteParams.cryptoCurrency,
      fiatCurrency: quoteParams.fiatCurrency,
      fiatAmount: amountValue,
      walletAddress: quoteParams.walletAddress,
      network: quoteParams.network,
    }));

    const quotes = await Promise.all(
      quoteRequests.map(async req => {
        try {
          return await getQuote(req);
        } catch (error) {
          if (error instanceof RampProviderError) {
            console.warn(`Quote failed for provider ${req.provider}:`, {
              message: error.message,
              statusCode: error.statusCode,
              response: error.response,
            });
          } else {
            console.error(`Unexpected error for provider ${req.provider}:`, error);
            toast({
              description: `${req.provider.charAt(0).toUpperCase() + req.provider.slice(1)}: An unexpected error occurred`,
              status: "error",
              isClosable: true,
              id: `quote-error-${req.provider}`,
            });
          }

          return null;
        }
      })
    );

    const filteredQuotes = quotes.filter((q): q is QuoteResponse => q !== null);

    const providers: Provider[] = filteredQuotes.map(quote => ({
      id: quote.provider,
      name: quote.provider,
      icon: quote.provider,
      fee: quote.estimatedNetworkFee.toString(),
      estimatedTime: quote.estimatedNetworkFee.toString(),
      price: {
        cryptoAmount: quote.cryptoAmount,
        usd: quote.fiatAmount,
      },
    }));

    providers.sort((a, b) => b.price.cryptoAmount - a.price.cryptoAmount);

    return providers;
  }, [debouncedAmount, toast, quoteParams]);

  const { data: providers = initialProviders, isFetching } = useQuery<Provider[]>({
    queryKey: [
      "getQuotes",
      debouncedAmount,
      quoteParams.cryptoCurrency,
      quoteParams.fiatCurrency,
      quoteParams.network,
      quoteParams.walletAddress,
    ],
    queryFn: fetchQuotes,
    enabled: !!debouncedAmount && parseFloat(debouncedAmount) > 0,
    refetchInterval: REFETCH_INTERVAL,
    retry: 3,
    refetchOnMount: true,
    staleTime: 0,
  });

  // Reset countdown every time a fetch happens
  useEffect(() => {
    if (!isFetching) {
      setCountdown(REFETCH_INTERVAL / 1000);
    }
  }, [isFetching]);

  // Decrement countdown every second
  useEffect(() => {
    if (!countdown || countdown <= 0) {
      return;
    }

    const interval = setInterval(() => {
      setCountdown(prev => Math.max(prev - 1, 0));
    }, 1000);

    return () => clearInterval(interval);
  }, [countdown]);

  const displayProviders: Provider[] =
    isFetching && providers.length === 0 ? initialProviders : providers;

  return (
    <ModalContent>
      <ModalHeader>
        <ModalBackButton payload={{ amount: amount, quoteParams }} />
        <ModalCloseButton />
        <Center flexDirection="column" gap="4px">
          <Heading size="xl">Select provider</Heading>
          <Text color="gray.700" fontSize="14px" fontWeight="400">
            Please choose a provider to complete your transaction
          </Text>
        </Center>
      </ModalHeader>

      <ModalBody>
        {amount && (
          <Box width="100%">
            {isFetching ? (
              <Text marginBottom="4" color={color("800")} fontSize="14px" fontWeight={600}>
                Fetching providers
              </Text>
            ) : (
              <Flex justifyContent="space-between" marginBottom="4">
                <Text color={color("800")} fontSize="14px" fontWeight={600}>
                  Pay with
                </Text>
                <Text color={color("800")} fontSize="14px" fontWeight={600}>
                  New quote : {countdown < 10 ? `0.0${countdown}` : `0.${countdown}`}
                </Text>
              </Flex>
            )}

            <VStack gap="12px" overflowY="auto" maxHeight="400px">
              {isFetching || displayProviders.length === 0 ? (
                <>
                  {[1, 2, 3].map(skeleton => (
                    <Box
                      key={`skeleton-${skeleton}`}
                      width="100%"
                      padding={4}
                      background="gray.50"
                      borderRadius="12px"
                    >
                      <Flex flexDirection="column" gap="3">
                        <Flex>
                          <Skeleton
                            width="66%"
                            height="18px"
                            marginRight="4"
                            endColor="gray.300"
                            startColor="gray.200"
                          />
                          <Skeleton
                            width="30%"
                            height="18px"
                            endColor="gray.300"
                            startColor="gray.200"
                          />
                        </Flex>
                        <Flex>
                          <Skeleton
                            width="66%"
                            height="18px"
                            marginRight="4"
                            endColor="gray.300"
                            startColor="gray.200"
                          />
                          <Skeleton
                            width="30%"
                            height="18px"
                            endColor="gray.300"
                            startColor="gray.200"
                          />
                        </Flex>
                      </Flex>
                    </Box>
                  ))}
                </>
              ) : (
                <>
                  {displayProviders.map((provider, index) => (
                    <Box
                      key={provider.id}
                      width="100%"
                      padding={4}
                      background="gray.50"
                      border={index === 0 ? `1px solid ${color("300")}` : "1px solid transparent"}
                      borderRadius="12px"
                      _hover={{ background: "gray.200" }}
                      cursor="pointer"
                      onClick={() => {
                        goBack({ provider: provider, amount: amount, quoteParams });
                      }}
                    >
                      <Flex alignItems="center" gap="10px">
                        <Icon as={getProviderIcon(provider.id)} boxSize="24px" />
                        <Flex flexDirection="column" flex="1">
                          <Flex alignItems="center" gap="2">
                            <Box>
                              <Text color={color("600")} fontWeight="400">
                                {provider.name.charAt(0).toUpperCase() + provider.name.slice(1)}
                              </Text>
                              {index === 0 && (
                                <Text color={color("green")} fontSize="14px" fontWeight="400">
                                  Best quote
                                </Text>
                              )}
                            </Box>
                            <Spacer />
                            <Box textAlign="right">
                              <Text color={color("600")} fontWeight="600">
                                {provider.price.cryptoAmount} {quoteParams.cryptoCurrency}
                              </Text>
                              <Text color={color("500")} fontSize="14px" fontWeight="400">
                                ≈${provider.price.usd.toFixed(2)}
                              </Text>
                            </Box>
                          </Flex>
                        </Flex>
                      </Flex>
                    </Box>
                  ))}
                </>
              )}
            </VStack>
          </Box>
        )}
      </ModalBody>
    </ModalContent>
  );
};
